<template>
  <v-card
      :width="$vuetify.breakpoint.smAndDown ? '100vw': cardWidth"
      :class="`elevation my-5 pa-0 ${showBorder ? 'px-2' : ''}`"
      :to="blogCardRouteTo"
      :style="blogCardStyles"
      flat
  >
    <div v-if="isBookmark" class="d-flex flex-row justify-space-between pb-1">
      <img :src="bookmarkedLabel" alt="bookemarked label" class="ml-4"/>
      <div class="d-flex flex-row">
        <v-menu right min-width="250px" offset-x rounded>
          <template v-slot:activator="{ on }">
            <img :src="forwardIcon" v-on="on" alt="bookemarked label" class="card-action-buttons mr-2" />
          </template>
          <v-card style="font-family: Montserrat, sans-serif;">
            <v-list-item-content>
              <div class="text-left d-flex flex-column">
                <v-list-item>
                  <v-btn
                      class="d-flex justify-space-between text-capitalize"
                      v-clipboard:copy="blogUrl"
                      v-clipboard:success="onBlogUrlCopy"
                      v-clipboard:error="onBlogUrlError"
                      block
                      text
                  >
                    <div class="font-weight-bold">Copy Link</div>
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item>
                <v-list-item
                    class="py-0 my-0"
                    v-for="(item, i) in shareToLinks"
                    :key="i"
                >
                  <v-btn block text class="text-left">
                    <a
                        class="text-decoration-none text-left text-capitalize"
                        style="color: black; width: 100%"
                        :href="`${item.href + blogUrl}`"
                        target="_blank"
                    >
                      Share to {{ item.name }}
                    </a>
                  </v-btn>
                </v-list-item>
              </div>
            </v-list-item-content>
          </v-card>
        </v-menu>
        <img :src="deleteIcon" alt="bookemarked label" class="card-action-buttons" @click="$emit('onDeleteBookmark', blog)"/>
      </div>
    </div>
    <v-progress-linear v-if="deleteBookmarkLoading" color="primary" class="mb-1" />
    <div class="image-card-container" v-if="cardImage">
      <v-img
          v-if="showCoverImage"
          :src="blog.image ? blog.image : defaultBlogCover"
          lazy-load="true"
          class="blog-image"
          width="100%"
          min-height="25vh"
      />
      <div class="new-tag-latest-blog pa-5" v-if="latestBlog">
        <h1
            class="my-2 text-truncate font-weight-bold"
            v-html="blog.title === '...' ? '' : blog.title"
            style="font-size: 2em"
        />
        <div class="mx-1 d-flex flex-row justify-space-between" style="font-size: 1.5em">
          <div class="text-capitalize">
            {{ getDisplayNameOrEmail(blog.owner) }}
            <v-icon v-if="isMentorApproved(blog.owner)" class="px-0 py-0 mt-0" color="green" small>
              mdi-check-decagram
            </v-icon>
          </div>
          <div class="d-flex flex-column">
            <div class="pa-1 text-center trending-note mt-auto">TRENDING</div>
          </div>
        </div>
      </div>
      <div class="new-tag" v-else>Read Now</div>
    </div>
    <div v-else>
      <router-link :to="`/blogs/${blog.slug}${isDraft ? '/edit' : ''}`" class="text-decoration-none">
        <div :class="`${!isBookmark ? 'mt-2' : ''} image-container`" >
          <v-img
              v-if="showCoverImage"
              :src="blog.image ? blog.image : defaultBlogCover"
              lazy-load="true"
              class="blog-image"
              width="100%"
          />
          <div class="new-tag" v-if="isNewBlog(blog.created)" :style="newTagStyles">New</div>
        </div>
        <v-card-text class="mx-0 px-0">
          <v-row align="start" justify="start">
            <v-col cols="12" lg="11" md="11">
              <h1 class="my-2 text-h5 text-truncate blog-title" v-html="blog.title === '...' ? '' : blog.title"/>
              <div class="mx-1 text-capitalize" style="color: #A0A0A0; font-size: 1.3em">
                {{ getDisplayNameOrEmail(blog.owner) }}
                <v-icon v-if="isMentorApproved(blog.owner)" class="px-0 py-0 mt-0" color="green" small>
                  mdi-check-decagram
                </v-icon>
              </div>
              <div class="d-flex flex-row justify-space-around mt-4">
                <div class="text-no-wrap blog-actions">
                  <img :src="viewsIcon" alt="views-icon" class="action-icon"/>
                  <div>
                    {{ kFormatter(blog.blog_views !== undefined ? blog.blog_views : 0) }}
                    <span class="hidden-sm-and-down mx-0 px-0">views</span>
                  </div>
                </div>
                <div class="text-no-wrap blog-actions">
                  <img :src="likeIcon" alt="likes-icon" class="action-icon" />
                  <div>
                    {{kFormatter(blog.blog_likes !== undefined ? blog.blog_likes : 0) }}
                    <span class="hidden-sm-and-down mx-0 px-0">likes</span>
                  </div>
                </div>
                <div class="text-no-wrap blog-actions">
                  <img :src="commentsIcon" alt="comments-icon" class="action-icon"/>
                  <div>
                    {{ kFormatter(blog.comments !== undefined ? blog.comments.length : 0) }}
                    <span class="hidden-sm-and-down mx-0 px-0">comments</span>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </router-link>
    </div>
  </v-card>
</template>

<script>
import {constructDate} from "@/utilz/date";
import {getDisplayNameOrEmail} from "@/utilz/dispalyNameOrEmail";
import {kFormatter} from "@/utilz/kFormatter";
import defaultBlogCover from "@/assets/default-blog-cover.png"
import commentsIcon from "@/assets/comments-icon.svg"
import likeIcon from "@/assets/likes-icon.svg"
import viewsIcon from "@/assets/views-icon.svg"
import bookmarkedLabel from "@/assets/bookmarked-label.svg"
import forwardIcon from "@/assets/forward-icon.svg"
import deleteIcon from "@/assets/delete-icon.svg"

export default {
  components: {},
  name: "BlogCard",
  props: {
    blog: {
      type: Object,
      default() {
        return {
          owner: {}
        }
      }
    },
    showCoverImage: {
      type: Boolean,
      default: true
    },
    isDraft: {
      type: Boolean,
      default: false
    },
    cardWidth: {
      type: String,
      default: "23%"
    },
    cardImage: {
      type: Boolean,
      default: false
    },
    latestBlog: {
      type: Boolean,
      default: false
    },
    isBookmark: {
      type: Boolean,
      default: false
    },
    showBorder: {
      type: Boolean,
      default: false
    },
    deleteBookmarkLoading: {
      type: Boolean,
      default: false
    }
  },
  data: () => {
    return {
      defaultBlogCover,
      likeIcon,
      commentsIcon,
      viewsIcon,
      forwardIcon,
      deleteIcon,
      bookmarkedLabel,
      shareToLinks: [
        {
          name: "Twitter",
          href: "https://twitter.com/share?url=",
        },
        {
          name: "LinkedIn",
          href: "https://www.linkedin.com/shareArticle?mini=true&url=",
        },
        {
          name: "Reddit",
          href: "https://reddit.com/submit?url=",
        },
        {
          name: "Hacker News",
          href: "https://news.ycombinator.com/submitlink?url=",
        },
        {
          name: "Facebook",
          href: "https://www.facebook.com/sharer.php?u=",
        },
      ],
    }
  },
  computed: {
    blogCardStyles() {
      return {
        backgroundColor: '#FFFFFF',
        ...this.showBorder ?
            {
              border: '.01em solid #C9C9C9',
              borderRadius: '.5em'
            } : {}
      }
    },
    blogCardRouteTo() {
      return `${this.isBookmark ? '' : `/blogs/${this.blog.slug}${this.isDraft ? '/edit' : ''}`}`
    },
    newTagStyles() {
      return { top: this.isBookmark ? '3em' : '1.5em' }
    }
  },
  methods: {
    isMentorApproved(blogOwner) {
      return blogOwner?.profle?.status?.toUpperCase() === "APPROVED"
    },
    blogCreatedAt(createdAt) {
      const date = new Date(createdAt);
      return constructDate(date.getDate(), date.getMonth(), date.getFullYear());
    },
    getDisplayNameOrEmail(user) {
      return getDisplayNameOrEmail(user);
    },
    kFormatter(value) {
      return kFormatter(value)
    },
    isNewBlog(dateCreated) {
      const now = new Date();
      return new Date(dateCreated) >= new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    }
  }
}
</script>

<style scoped lang="scss">
.card-action-buttons {
  width: 1em;
  cursor: pointer
}

.trending-note {
  background-color: #FFFFFF;
  border-radius: .3em;
  color: #0033AA;
  font-weight: bold;
  font-size: .7em;
  height: auto;
}

.image-container {
  height: 10em;
  border-radius: .5em;
  width: 100%;

  .blog-image {
    position: relative;
    contain: inherit;
    height: inherit;
    width: 100%;
    border-radius: .5em;
  }
  .new-tag {
    position: absolute;
    background-color: #FFFFFF;
    color: #0035B0;
    text-transform: uppercase;
    font-weight: bold;
    padding: .5em 1.4em;
    border-radius: 0 .5em .5em 0;
  }
}

.image-card-container {
  height: 100%;
  border-radius: .5em;
  width: 100%;
  elevation: higher;
  background-color: rgba(27, 70, 144, 0.5);

  .blog-image {
    position: relative;
    contain: inherit;
    height: inherit;
    width: 100%;
    border-radius: .5em;
    opacity: 0.1;
  }
  .new-tag {
    position: absolute;
    background-color: #FFFFFF;
    bottom: 1.5em;
    color: #0035B0;
    text-transform: uppercase;
    font-weight: bold;
    padding: .5em 1.4em;
    border-radius: 0 .5em .5em 0;
  }
  .new-tag-bookmark {
    position: absolute;
    bottom: 1.8em;
    background-color: #FFFFFF;
    color: #0035B0;
    text-transform: uppercase;
    font-weight: bold;
    padding: .5em 1.4em;
    border-radius: 0 .5em .5em 0;
  }
  .new-tag-latest-blog {
    position: absolute;
    bottom: 0;
    background-color: transparent;
    color: #FFFFFF !important;
    text-transform: none;
    width: 100%;
  }
}

.blog-title {
  color: #002B8E;
  font-size: 2.4em;
  font-weight: 420;
}

.blog-actions {
  background-color: rgba(217, 217, 217, 0.37);
  border-radius: .5em;
  color: #8B8585;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: 30%;
  font-size: .9em;

  .action-icon {
    width: 1.3em !important;
    height: 1.3em !important;
    padding-right: .3em;
  }
}
</style>
